import React, {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import { useNavigate } from "react-router-dom";

import { GlobalContext, LoaderContext } from "@/helpers/contexts/contexts";
import { HTTP_STATUS } from "@/config/constants";
import { logger } from "@/helpers/loggerHelper";
import { generatePath, ROUTES } from "@/helpers/routes/routePaths";
import useIsParticipant from "@/features/auth/hooks/useIsParticipant";
import { nationalPlusUpCampaignService } from "../services/nationalPlusUpCampaignService";
import {
  buildNationalPlusUpDraftPayload,
  getNationalPlusUpInitialValues,
} from "../utils/nationalPlusUpHelpers";
import NationalPlusUpJoinCampaignForm from "./NationalPlusUpJoinCampaignForm";

type NationalPlusUpRecord = Record<string, any>;

type NationalPlusUpJoinCampaignProps = {
  campaign?: NationalPlusUpRecord;
};

type SaveDraftArgs = {
  selections?: NationalPlusUpRecord[];
  isDraft?: boolean;
  removeOrderFromDraftOrderIds?: string[];
};

function NationalPlusUpJoinCampaign({
  campaign,
}: NationalPlusUpJoinCampaignProps) {
  const navigate = useNavigate();
  const loader = useContext(LoaderContext) as (_loading: boolean) => void;
  const { loggedInUser, selectedBrand } = useContext(GlobalContext) as {
    loggedInUser?: NationalPlusUpRecord;
    selectedBrand?: NationalPlusUpRecord;
  };
  const isParticipant = useIsParticipant();
  const [draftData, setDraftData] = useState(null);
  const campaignId = campaign?.campaignId;
  const participantId = loggedInUser?.participantData?.id;

  const initialValues = useMemo(() => {
    return getNationalPlusUpInitialValues(campaign, draftData);
  }, [campaign, draftData]);

  const fetchDraftData = useCallback(
    async (campaignId: string | number) => {
      try {
        loader(true);
        const res =
          await nationalPlusUpCampaignService.getDraftNationalPlusUpCampaign({
            campaignId,
            fkParticipantId: participantId,
          });
        if (res.status === HTTP_STATUS.OK) {
          const data = res.data;
          if (String(data?.campaignId) === String(campaignId)) {
            setDraftData(data);
          }
        }
      } catch (err) {
        logger.error("Fetch National Plus Up draft data error", err);
      } finally {
        loader(false);
      }
    },
    [loader, participantId]
  );

  const saveDraft = useCallback(
    async ({
      selections,
      isDraft,
      removeOrderFromDraftOrderIds = [],
    }: SaveDraftArgs) => {
      const payload = buildNationalPlusUpDraftPayload({
        campaign,
        selectedBrand,
        loggedInUser,
        selections,
        isDraft,
        removeOrderFromDraftOrderIds,
      });

      try {
        loader(true);
        const res =
          await nationalPlusUpCampaignService.draftNationalPlusUpCampaign(
            payload
          );
        if (res.status === HTTP_STATUS.OK) {
          if (isDraft) {
            navigate(ROUTES.LAUNCH_DIRECT_MAIL, { replace: true });
            return;
          }
          navigate(generatePath.nationalPlusUpConfirm(campaignId), {
            replace: true,
            state: { campaignId },
          });
        }
      } catch (err) {
        logger.error("National Plus Up draft save error", err);
      } finally {
        loader(false);
      }
    },
    [campaign, campaignId, loggedInUser, selectedBrand, loader, navigate]
  );

  useEffect(() => {
    if (campaignId && isParticipant && participantId) {
      fetchDraftData(campaignId);
    }
  }, [campaignId, fetchDraftData, isParticipant, participantId]);

  return (
    <NationalPlusUpJoinCampaignForm
      campaign={campaign}
      initialValues={initialValues}
      onSubmit={saveDraft}
      draftOrderIds={draftData?.orderIds}
    />
  );
}

export default NationalPlusUpJoinCampaign;
